import { useEffect, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { automationFilesQueryOptions } from "@/lib/data/react-query/automations";
import { startManualAutomation } from "@/lib/data/requests/automations";
import { automationError } from "./automationError";

export function AutomationRunPicker({
  id,
  onStarted,
}: {
  id: string;
  onStarted: () => void;
}) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [selection, setSelection] = useState<{ id: string; key: string }>();
  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search);
    }, 250);
    return () => {
      clearTimeout(timer);
    };
  }, [search]);
  const files = useInfiniteQuery(automationFilesQueryOptions(query));
  const client = useQueryClient();
  const run = useMutation({
    mutationFn: ({ id: fileId, key }: { id: string; key: string }) =>
      startManualAutomation(id, fileId, key),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["automation-runs", id] });
      onStarted();
    },
  });
  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        Choose a processed file. This starts a new run using the automation’s
        current instructions and allowed changes.
      </p>
      <Input
        aria-label="Find a processed file"
        placeholder="Search file names…"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setSelection(undefined);
          run.reset();
        }}
        maxLength={200}
        disabled={run.isPending}
      />
      {files.isPending && <p role="status">Loading files…</p>}
      {files.error && (
        <p role="alert" className="text-destructive">
          {automationError(files.error)}
        </p>
      )}
      <div className="max-h-80 space-y-2 overflow-y-auto">
        {files.data?.pages
          .flatMap((page) => page.data)
          .map((file) => (
            <label
              key={file.id}
              className="flex cursor-pointer items-start gap-3 rounded-lg border p-3"
            >
              <input
                className="mt-1"
                type="radio"
                name="automation-file"
                checked={selection?.id === file.id}
                disabled={run.isPending}
                onChange={() => {
                  run.reset();
                  setSelection({ id: file.id, key: crypto.randomUUID() });
                }}
              />
              <span className="min-w-0">
                <span className="block truncate font-medium">
                  {file.fileName}
                </span>
                <span className="text-muted-foreground block text-xs">
                  {file.sourceKind === "firm" ? "Firm file" : "Case file"}
                </span>
                {file.documentDescription && (
                  <span className="text-muted-foreground mt-1 line-clamp-2 block text-sm">
                    {file.documentDescription}
                  </span>
                )}
              </span>
            </label>
          ))}
      </div>
      {!files.isPending &&
        !files.error &&
        !files.data.pages.some((page) => page.data.length) && (
          <p className="text-muted-foreground text-sm">
            No processed files found.
          </p>
        )}
      {files.hasNextPage && (
        <Button
          variant="outline"
          disabled={files.isFetchingNextPage}
          onClick={() => void files.fetchNextPage()}
        >
          More files
        </Button>
      )}
      {run.error && (
        <p role="alert" className="text-destructive">
          {automationError(run.error)}
        </p>
      )}
      <div className="flex justify-end border-t pt-4">
        <Button
          disabled={!selection || run.isPending}
          onClick={() => {
            if (selection) run.mutate(selection);
          }}
        >
          {run.isPending ? "Starting…" : "Run on this file"}
        </Button>
      </div>
    </div>
  );
}
